import styled from '@emotion/styled';
import { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    IAgentUserForList,
    USER_ROLE_LABEL,
    PROFILE_STAGE,
    COMPANY_USER_ROLE,
} from 'domain/entities/AgentUser/AgentUser';
import { UserIcon } from 'ui/components/elements/UserIcon/UserIcon';
import { PageTitle } from 'ui/components/elements/PageTitle/PageTitle';
import { AboutService } from 'ui/components/company/features/AboutService';
import { agentUserContext } from 'application/contexts/useAgentUser';
import { mqUser } from 'utils/responsive';

const tabs = ['メンバーの管理', '招待について'];

const descriptions: { [key: string]: string[] } = {
    メンバーの管理: ['このページでは、貴社内でのメンバーの管理を行うことができます。'],
    招待について: [
        '社内に本サービスを利用したいほかのメンバーがいる場合、こちらから招待を行うことが可能です。',
        '招待を行うと、同時にマイページを確認できるようになります。',
    ],
};

const Wrapper = styled.div`
    padding: 18px 50px;
    display: flex;
    flex-direction: column;
    margin: 0 auto;
    text-align: left;
    background-color: #f8f9fd;
    height: auto;
    min-height: calc(100vh - var(--height-pc-header));

    ${mqUser('SP')} {
        padding: 18px 15px;
        min-height: calc(100vh - var(--height-pc-header));
    }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    border-radius: 10px;
    overflow: hidden;
    background-color: var(--white);
    margin-bottom: 50px;
`;

const TableHeader = styled.th`
    padding: 10px;
    background-color: var(--main-font-color);
    border: 1px solid var(--gray);
    text-align: left;
    color: var(--white);
    font-size: var(--font-medium);

    ${mqUser('SP')} {
        font-size: var(--font-small);
    }
`;

const TableCell = styled.td`
    padding: 10px;

    ${mqUser('SP')} {
        font-size: var(--font-small);
    }
`;

const TableRow = styled.tr`
    &:nth-of-type(even) {
        background-color: var(--light-gray);
    }
`;

const TopContainer = styled.div`
    display: flex;
    justify-content: space-between;
    width: 100%;

    ${mqUser('SP')} {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
`;

const NameCell = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
`;

const Button = styled.button`
    color: var(--white);
    border: none;
    font-weight: 700;

    border-radius: 5px;
    padding: 8px 10px;
    cursor: pointer;
`;

const BlueButton = styled(Button)`
    background-color: var(--main-color-blue);
    margin: 0 auto;
    font-size: var(--font-medium);

    ${mqUser('SP')} {
        width: 100%;
    }
`;

const PrimaryButton = styled(Button)`
    background-color: var(--primary);
    font-size: var(--font-large);
    margin-right: 10px;
    height: 40px;
    align-self: center;

    ${mqUser('SP')} {
        font-size: var(--font-medium);
        align-self: stretch;
        margin: 0 auto 20px 0;
    }
`;

export const MemberTable: React.FC<{
    memberList: IAgentUserForList[];
    openModal: () => void;
    openNeedProfileModal: () => void;
}> = ({ memberList, openModal, openNeedProfileModal }) => {
    const navigate = useNavigate();
    const { user } = useContext(agentUserContext);
    if (!user) {
        return null;
    }
    const user_registration_complete = user.account_profile_stage === PROFILE_STAGE.COMPLETE;

    const onClickInvite = () => {
        if (!user_registration_complete) {
            openNeedProfileModal();
        } else {
            openModal();
        }
    };

    return (
        <Wrapper>
            <TopContainer>
                <PageTitle title="メンバー管理" />
                {user.role >= COMPANY_USER_ROLE.SUPER_USER && (
                    <PrimaryButton
                        onClick={() => {
                            onClickInvite();
                        }}
                    >
                        招待する
                    </PrimaryButton>
                )}
            </TopContainer>
            <AboutService tabs={tabs} descriptions={descriptions} />

            <Table>
                <thead>
                    <tr>
                        <TableHeader>氏名</TableHeader>
                        <TableHeader>メールアドレス</TableHeader>
                        <TableHeader>ロール</TableHeader>
                        <TableHeader></TableHeader>
                    </tr>
                </thead>
                <tbody>
                    {memberList.map((member) => (
                        <TableRow key={member.id}>
                            <TableCell>
                                <NameCell>
                                    <UserIcon
                                        border
                                        src={member.icon_image}
                                        alt={`${member.last_name || ''} ${member.first_name || ''}`}
                                    />
                                    {`${member.last_name || ''} ${member.first_name || ''}`}
                                </NameCell>
                            </TableCell>
                            <TableCell>{member.email}</TableCell>
                            <TableCell>{USER_ROLE_LABEL[member.role as keyof typeof USER_ROLE_LABEL]}</TableCell>
                            <TableCell>
                                {(user.role >= COMPANY_USER_ROLE.SUPER_USER || user.id === member.id) && (
                                    <BlueButton
                                        onClick={() => {
                                            navigate(`/agent/member/edit/${member.id}`);
                                        }}
                                    >
                                        編集
                                    </BlueButton>
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </tbody>
            </Table>
        </Wrapper>
    );
};
